// Mobile sticky add-to-cart bar (issue #18).
//
// Additive only, same pattern as assets/sj-faq-accordion.js: this file is
// wired into sections/main-product.liquid as a plain <script defer> next to
// the buy_buttons block, and touches no vendored Dawn file.
//
// Three jobs:
//   1. Show the bar once (not before) the buy panel has scrolled past, via
//      an IntersectionObserver on a sentinel rendered right after the
//      buy-buttons block.
//   2. Keep the bar's variant name / price / availability in sync with
//      Dawn's own variant-change pipeline (PUB_SUB_EVENTS.variantChange,
//      published by product-info.js) rather than polling or re-implementing
//      variant/price logic.
//   3. Nothing else — the bar's button submits Dawn's existing product form
//      via the standard HTML `form="..."` attribute (see the button markup
//      in main-product.liquid), so product-form.js's own submit handler
//      runs unmodified and the cart drawer opens exactly as it does today.
//      There is no second add-to-cart path here.
(function () {
  var MOBILE_QUERY = '(max-width: 749px)';

  document.querySelectorAll('[data-sj-sticky-atc]').forEach(function (bar) {
    var sectionId = bar.dataset.sectionId;
    var sentinel = document.querySelector(
      '[data-sj-sticky-atc-sentinel="' + sectionId + '"]'
    );
    if (!sentinel) return;

    // Relocate to a direct child of <body>, found and fixed during
    // centralized browser verification (issue #18): the bar is rendered
    // inside main-product.liquid's `.product__info-wrapper`, which Dawn's
    // own scroll-reveal system (`.scroll-trigger.animate--slide-in`,
    // component-sujuud-motion.css) puts a `transform` on — its keyframe's
    // settled end state is `transform: translateY(0)`, a transform
    // *function*, not the `none` keyword, so per spec it establishes a
    // containing block for `position: fixed` descendants permanently, not
    // just mid-animation. Left in place, `bottom: 0` etc. resolve against
    // that ancestor's box instead of the real viewport, so the bar renders
    // off-screen and never shows no matter what the observer decides. The
    // sentinel stays put — it only needs an accurate position in the
    // document to be observed, not viewport-relative fixed positioning.
    // The button's `form="..."` attribute associates it with Dawn's
    // product form by id regardless of DOM position, so moving the bar
    // doesn't affect submission.
    document.body.appendChild(bar);

    var variantEl = bar.querySelector('[data-sj-sticky-atc-variant]');
    var priceEl = bar.querySelector('[data-sj-sticky-atc-price]');
    var button = bar.querySelector('[data-sj-sticky-atc-button]');
    var buttonTextEl = bar.querySelector('[data-sj-sticky-atc-button-text]');
    var notifyLink = bar.querySelector('[data-sj-sticky-atc-notify]');

    // --- 1. Show/hide -------------------------------------------------
    //
    // Ported from issue #18 / sujuud-website's ProductView.tsx, including
    // the fix the source already made rather than rediscovering it: a plain
    // `!entry.isIntersecting` shows the bar on first paint too, because
    // isIntersecting is false both *before* the sentinel has been reached
    // and *after* it's been scrolled past — those are the only two states
    // for a sentinel with a top rootMargin, since it starts below the
    // viewport. Disambiguating with boundingClientRect.top < 0 (the
    // sentinel is now above the viewport, i.e. actually passed, not merely
    // not-yet-reached) is what the source's fix does, ported verbatim.
    var observer = null;

    function handleIntersect(entries) {
      var entry = entries[0];
      var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('sj-sticky-atc--visible', passed);
    }

    function attachObserver() {
      if (observer) return;
      observer = new IntersectionObserver(handleIntersect, {
        rootMargin: '-80px 0px 0px 0px',
      });
      observer.observe(sentinel);
    }

    function detachObserver() {
      if (!observer) return;
      observer.disconnect();
      observer = null;
      bar.classList.remove('sj-sticky-atc--visible');
    }

    var mql = window.matchMedia(MOBILE_QUERY);
    function syncToBreakpoint() {
      if (mql.matches) {
        attachObserver();
      } else {
        detachObserver();
      }
    }
    syncToBreakpoint();
    // Desktop never needs the observer running, and a device can cross the
    // 750px breakpoint mid-session (rotation, resizing a browser window) —
    // attach/detach rather than checking matchMedia only once.
    if (mql.addEventListener) {
      mql.addEventListener('change', syncToBreakpoint);
    } else if (mql.addListener) {
      // Safari < 14.
      mql.addListener(syncToBreakpoint);
    }

    // --- 2. Variant / price / availability sync ------------------------
    //
    // product-info.js publishes PUB_SUB_EVENTS.variantChange after it swaps
    // the buy panel's own #price-<section> node and resolves the sold-out
    // state of #ProductSubmitButton-<section> from the freshly-fetched
    // section HTML. Reusing that same fetched HTML here — rather than
    // recomputing money formatting or sold-out logic — guarantees the bar
    // never drifts from what the buy panel itself just rendered.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data || event.data.sectionId !== sectionId) return;
        var variant = event.data.variant;
        var html = event.data.html;

        if (variantEl) {
          variantEl.textContent =
            variant && variant.title !== 'Default Title' ? variant.title : '';
        }

        if (priceEl && html) {
          var priceSource = html.getElementById('price-' + sectionId);
          if (priceSource) priceEl.innerHTML = priceSource.innerHTML;
        }

        var soldOut = true;
        if (html) {
          var sourceButton = html.getElementById('ProductSubmitButton-' + sectionId);
          soldOut = !(sourceButton && !sourceButton.hasAttribute('disabled'));
        }
        if (button) {
          button.hidden = soldOut;
          if (!soldOut) {
            button.toggleAttribute('disabled', false);
            if (buttonTextEl && window.variantStrings) {
              buttonTextEl.textContent = !variant
                ? window.variantStrings.unavailable
                : window.variantStrings.addToCart;
            }
          }
        }
        if (notifyLink) notifyLink.hidden = !soldOut;
      });
    }

    // --- 3. Mirror the primary submit button's transient loading state -
    //
    // product-form.js's submit handler (unmodified — this file never
    // touches it) only ever manipulates the buy panel's own submit button
    // (`this.submitButton`), since it has no knowledge of this bar. Without
    // this, tapping "Add to cart" here gives no feedback while the AJAX
    // add-to-cart request is in flight. Mirroring the primary button's own
    // `aria-disabled`/`loading` state is observation only — it does not
    // gate or duplicate the submission itself, which still runs exactly
    // once through the one <form>, the one product-form.js handler.
    var primaryButton = document.getElementById('ProductSubmitButton-' + sectionId);
    var mySpinner = button && button.querySelector('.loading__spinner');
    if (primaryButton && button) {
      var mirrorLoadingState = function () {
        var loading = primaryButton.classList.contains('loading');
        button.classList.toggle('loading', loading);
        if (mySpinner) mySpinner.classList.toggle('hidden', !loading);
        if (primaryButton.getAttribute('aria-disabled') === 'true') {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      };
      new MutationObserver(mirrorLoadingState).observe(primaryButton, {
        attributes: true,
        attributeFilter: ['class', 'aria-disabled'],
      });
    }
  });
})();
